/**
 * GDACS disaster alert data source.
 * Fetches active disaster alerts from the Global Disaster Alerting Coordination System RSS feed.
 * Free, no API key required. Public domain.
 */

import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent, Severity, EventType } from '../base-source';
import { getApiBase } from '@/app/api-router';
import { stripHtml } from '@/utils/sanitize';
import { djb2Hash } from '@/utils/hash';

const SOURCE_ID = 'gdacs';

function alertLevelToSeverity(level: string): Severity {
  const normalized = level.trim().toLowerCase();
  if (normalized === 'red') return 'critical';
  if (normalized === 'orange') return 'high';
  if (normalized === 'green') return 'medium';
  return 'info';
}

/** Map GDACS event type codes to our EventType. */
function gdacsEventType(code: string): EventType {
  const upper = code.toUpperCase();
  if (upper === 'TC') return 'weather'; // tropical cyclone
  if (upper === 'EQ') return 'earthquake';
  if (upper === 'FL') return 'disaster'; // flood
  if (upper === 'VO') return 'disaster'; // volcano
  if (upper === 'DR') return 'disaster'; // drought
  return 'disaster';
}


let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

export const gdacsSource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'GDACS',
    description: 'Global Disaster Alerting Coordination System — UN disaster alerts',
    icon: 'alert-triangle',
    category: 'environmental',
    updateInterval: 1_800_000, // 30 min
    requiresApiKey: false,
    dataLicense: 'Public Domain',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
  },

  async start(): Promise<void> {
    status.state = 'active';
    console.log('[Sentinel] GDACS disaster alert source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
  },

  async fetch(): Promise<NormalizedEvent[]> {
    try {
      // Route through proxy for CORS
      const url = `${getApiBase()}/api/data/gdacs`;

      const response = await fetch(url, { signal: AbortSignal.timeout(15_000) });

      if (!response.ok) {
        throw new Error(`GDACS feed returned ${response.status}`);
      }

      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/xml');

      const parseError = doc.querySelector('parsererror');
      if (parseError) {
        throw new Error('GDACS XML parse error');
      }

      const items = doc.querySelectorAll('item');
      const events: NormalizedEvent[] = [];

      for (const item of items) {
        const title = item.querySelector('title')?.textContent?.trim() ?? '';
        const description = item.querySelector('description')?.textContent ?? '';
        const link = item.querySelector('link')?.textContent?.trim() ?? '';
        const pubDate = item.querySelector('pubDate')?.textContent?.trim() ?? '';
        const guid = item.querySelector('guid')?.textContent?.trim() ?? '';

        // Extract GDACS-specific namespace elements
        const alertLevel = item.getElementsByTagName('gdacs:alertlevel')[0]?.textContent?.trim() ?? '';
        const eventType = item.getElementsByTagName('gdacs:eventtype')[0]?.textContent?.trim() ?? '';
        const country = item.getElementsByTagName('gdacs:country')[0]?.textContent?.trim() ?? '';
        const severity = item.getElementsByTagName('gdacs:severity')[0]?.textContent?.trim() ?? '';

        // Extract location from geo:Point or georss:point
        let lat: number | null = null;
        let lng: number | null = null;

        const geoPoint = item.getElementsByTagName('geo:Point')[0];
        if (geoPoint) {
          const geoLat = geoPoint.getElementsByTagName('geo:lat')[0]?.textContent;
          const geoLng = geoPoint.getElementsByTagName('geo:long')[0]?.textContent;
          if (geoLat && geoLng) {
            lat = parseFloat(geoLat);
            lng = parseFloat(geoLng);
          }
        }

        if (lat === null || lng === null) {
          const georssPoint = item.getElementsByTagName('georss:point')[0]?.textContent;
          if (georssPoint) {
            const parts = georssPoint.trim().split(/\s+/);
            if (parts.length >= 2) {
              lat = parseFloat(parts[0]);
              lng = parseFloat(parts[1]);
            }
          }
        }

        const eventId = guid ? `gdacs-${djb2Hash(guid)}` : `gdacs-${djb2Hash(link || title)}`;

        const event: NormalizedEvent = {
          id: eventId,
          source: SOURCE_ID,
          timestamp: pubDate ? new Date(pubDate).toISOString() : new Date().toISOString(),
          type: gdacsEventType(eventType),
          title: title || 'GDACS Alert',
          description: stripHtml(description),
          severity: alertLevelToSeverity(alertLevel),
          tags: ['disaster', eventType || 'unknown', ...(country ? [country] : [])],
          links: link ? [{ url: link, label: 'GDACS Detail' }] : [],
          raw: {
            alertLevel,
            eventType,
            severity: severity,
            country,
          },
        };

        if (lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng)) {
          event.location = { lat, lng, country, name: country || undefined };
        }

        events.push(event);
      }

      status.state = 'active';
      status.lastFetch = new Date().toISOString();
      status.eventCount = events.length;
      return events;
    } catch (err) {
      status.state = 'error';
      status.lastError = err instanceof Error ? err.message : String(err);
      throw err;
    }
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};
